import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface RevenueStream {
  id: string
  name: string
  current_revenue: number
  previous_revenue: number
  growth_rate: number
  margin: number
  customer_count: number
  trend: string
}

interface RevenueTrend {
  id: string
  month: string
  year: number
  total_revenue: number
  recurring_revenue: number
  one_time_revenue: number
  new_revenue: number
}

interface RevenueForecast {
  id: string
  period: string
  forecast_revenue: number
  best_case: number
  worst_case: number
  confidence: number
}

interface Statistics {
  total_revenue: number
  mrr: number
  arr: number
  revenue_growth: number
  net_revenue_retention: number
  avg_revenue_per_customer: number
}

export default function RevenueAnalytics2Page() {
  const [streams, setStreams] = useState<RevenueStream[]>([])
  const [trends, setTrends] = useState<RevenueTrend[]>([])
  const [forecasts, setForecasts] = useState<RevenueForecast[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [streamsRes, trendsRes, forecastsRes, statsRes] = await Promise.all([
        fetch('/api/revenueanalytics/trend'),
        fetch('/api/revenueanalytics/transactions'),
        fetch('/api/revenueanalytics/forecasts'),
        fetch('/api/revenueanalytics/statistics')
      ])
      if (streamsRes.ok) setStreams(await streamsRes.json())
      if (trendsRes.ok) setTrends(await trendsRes.json())
      if (forecastsRes.ok) setForecasts(await forecastsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching revenue analytics data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Revenue Analytics</h1>
          <p className="mt-2 text-gray-600">Phase 231 - Revenue breakdown, trends, and forecasts</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(statistics.total_revenue)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">MRR</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(statistics.mrr)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">ARR</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(statistics.arr)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Revenue Growth</p>
              <p className="text-2xl font-bold text-purple-600">+{statistics.revenue_growth}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">NRR</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.net_revenue_retention}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Revenue/Customer</p>
              <p className="text-2xl font-bold text-indigo-600">{formatCurrency(statistics.avg_revenue_per_customer)}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Revenue Streams</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stream</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Previous</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Growth</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Margin</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customers</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trend</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {streams.map((stream) => (
                  <tr key={stream.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{stream.name}</td>
                    <td className="px-6 py-4 text-blue-600 font-bold">{formatCurrency(stream.current_revenue)}</td>
                    <td className="px-6 py-4 text-gray-500">{formatCurrency(stream.previous_revenue)}</td>
                    <td className={`px-6 py-4 ${stream.growth_rate >= 0 ? 'text-green-600' : 'text-red-600'}`}>{stream.growth_rate > 0 ? '+' : ''}{stream.growth_rate}%</td>
                    <td className="px-6 py-4 text-purple-600">{stream.margin}%</td>
                    <td className="px-6 py-4 text-gray-700">{stream.customer_count}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${stream.trend === 'up' ? 'bg-green-100 text-green-800' : stream.trend === 'down' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>{stream.trend}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Revenue Forecasts</h2>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {forecasts.map((forecast) => (
              <div key={forecast.id} className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-900 mb-2">{forecast.period}</p>
                <p className="text-2xl font-bold text-blue-600">{formatCurrency(forecast.forecast_revenue)}</p>
                <div className="mt-2 text-sm">
                  <div className="flex justify-between text-gray-500">
                    <span>Best Case:</span>
                    <span className="text-green-600">{formatCurrency(forecast.best_case)}</span>
                  </div>
                  <div className="flex justify-between text-gray-500">
                    <span>Worst Case:</span>
                    <span className="text-red-600">{formatCurrency(forecast.worst_case)}</span>
                  </div>
                  <div className="flex justify-between text-gray-500">
                    <span>Confidence:</span>
                    <span className="text-purple-600">{forecast.confidence}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Revenue Trends</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Revenue</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recurring</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">One-Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">New Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {trends.slice(0, 6).map((trend) => (
                  <tr key={trend.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{trend.month} {trend.year}</td>
                    <td className="px-6 py-4 font-bold text-gray-900">{formatCurrency(trend.total_revenue)}</td>
                    <td className="px-6 py-4 text-blue-600">{formatCurrency(trend.recurring_revenue)}</td>
                    <td className="px-6 py-4 text-purple-600">{formatCurrency(trend.one_time_revenue)}</td>
                    <td className="px-6 py-4 text-green-600">{formatCurrency(trend.new_revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
